/**
 * 詳細レポートの「解析の履歴」(docs/27 §16-4)。
 *
 * 地点の解析の記録 (`posts/{postId}/analysisRuns`) を新しい順に並べ、解析日時・由来・方式・件数・解析時の
 * 出力フォーマットとカテゴリマスタの版・モデルを示す。「AI に送った内容」を開くと、記録したプロンプトの
 * 文面を地区のスナップショット (`districts/{districtId}/analysisSnapshots`) から読んで表示する。読込は開いたときに 1 回だけ。
 *
 * docs/98 R-97 項目 5: 2 つの記録を選んで比べる (件数・評価の内訳・調査項目の値ごとの件数・解析時の設定の違い)。
 * プロンプトに載せたカテゴリマスタは、解析時の中身 (行の一覧) を開いて見られる。
 */
import { Card } from '@citizen-walk/shared-ui';
import {
  analysisSnapshotsCollectionPath,
  ANALYSIS_RUN_ORIGIN_LABELS,
  ANALYSIS_RUNS_COLLECTION,
  STREET_SURVEY_PIPELINE_ID,
  POLARITY_LABELS,
  type AiCategoryMasterRow,
  type AnalysisRunDoc,
  type AnalysisRunMasterRef,
  type AnalysisRunPromptRef,
  type AnalysisRunSourceRef,
  type EvaluationAxisDef,
} from '@citizen-walk/shared-types';
import { compareAnalysisRuns, runItems } from './analysisRunComparison';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

export interface StreetSurveyAnalysisHistoryCardProps {
  districtId: string;
  postId: string;
  /** 投稿に表示中の結果の記録 (`aiAnalysis.runId`)。 */
  currentRunId: string | null;
  /**
   * 表示する調査 (docs/98 R-97)。追加の調査はその key の記録だけ、既定の調査は追加の調査の key 以外の記録
   * (既定の調査の記録の surveyKey は出力フォーマット ID など)。未指定なら全件。
   */
  surveyKey?: string;
  additionalSurveyKeys?: readonly string[];
  /** 比べるときの評価の軸 (評価の内訳)。 */
  evaluation?: EvaluationAxisDef | null;
  /** 比べるときに値ごとの件数を数える列 (選んだ調査項目の codeField) と、値 → 表示名。 */
  valueField?: string | null;
  valueLabel?: string;
  labelOf?: (value: string) => string;
}

interface RunRow {
  id: string;
  run: AnalysisRunDoc;
}

type LoadState = { status: 'loading' } | { status: 'error' } | { status: 'ready'; rows: RunRow[] };

function formatDateTime(iso: string | null): string {
  if (!iso) return '不明';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '不明' : d.toLocaleString('ja-JP');
}

function formatSource(ref: AnalysisRunSourceRef): string {
  const name = ref.name ?? '名称なし';
  const version = ref.version !== null ? `第 ${ref.version} 版` : '版不明';
  const updated = ref.updatedAt
    ? `、${new Date(ref.updatedAt).toLocaleDateString('ja-JP')} 更新`
    : '';
  return `${name}（${version}${updated}）`;
}

function pipelineLabel(pipeline: string | null): string {
  if (pipeline === STREET_SURVEY_PIPELINE_ID) return '新方式';
  if (pipeline) return '従来の方式';
  return '不明';
}

function itemCount(run: AnalysisRunDoc): number | null {
  const generic = run.aiAnalysis?.generic as Record<string, unknown> | undefined;
  if (!generic) return null;
  // 記録の itemsKey (新方式) を優先し、無ければ generic の最初の配列 (docs/98 R-98 低)。
  return runItems(run).length;
}

/** 解析時のカテゴリマスタの中身 (行の一覧) をスナップショットから読んで表示する。 */
function MasterSnapshotView({
  districtId,
  master,
}: {
  districtId: string;
  master: AnalysisRunMasterRef;
}) {
  const [rows, setRows] = useState<AiCategoryMasterRow[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    if (!master.snapshotId) return;
    let cancelled = false;
    getDoc(doc(db, analysisSnapshotsCollectionPath(districtId), master.snapshotId))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.data() as { data?: { rows?: AiCategoryMasterRow[] }; tooLarge?: boolean };
        if (!data) setMessage('中身を読み込めませんでした。');
        else if (data.tooLarge) setMessage('中身が大きすぎるため保存していません。');
        else setRows(data.data?.rows ?? []);
      })
      .catch(() => {
        if (!cancelled) setMessage('中身を読み込めませんでした。');
      });
    return () => {
      cancelled = true;
    };
  }, [districtId, master.snapshotId]);
  if (!master.snapshotId) {
    return <p className="mt-1 text-[11px] text-slate-500">中身は記録されていません。</p>;
  }
  if (message) return <p className="mt-1 text-[11px] text-slate-500">{message}</p>;
  if (!rows) return <p className="mt-1 text-[11px] text-slate-500">読込中…</p>;
  return (
    <div className="mt-1 max-h-72 overflow-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="pr-2 font-normal">コード</th>
            <th className="pr-2 font-normal">名前</th>
            <th className="pr-2 font-normal">定義・判断基準</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={`${r.majorCd ?? ''}-${r.categoryCd}-${i}`}
              className="align-top border-t border-slate-100"
            >
              <td className="pr-2 whitespace-nowrap">
                {r.majorCd ? `${r.majorCd}-` : ''}
                {r.categoryCd}
              </td>
              <td className="pr-2">{r.category}</td>
              <td className="pr-2 text-slate-600">
                {[r.definition, r.criteria].filter(Boolean).join(' ／ ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function polarityText(
  counts: { positive: number; negative: number; neutral: number } | null,
  labels: Record<'positive' | 'negative' | 'neutral', string>,
): string {
  if (!counts) return '—';
  return `${labels.positive} ${counts.positive} / ${labels.negative} ${counts.negative} / ${labels.neutral} ${counts.neutral}`;
}

function PromptText({ districtId, prompt }: { districtId: string; prompt: AnalysisRunPromptRef }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, analysisSnapshotsCollectionPath(districtId), prompt.systemInstructionSnapshotId))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.data() as { text?: string | null; tooLarge?: boolean } | undefined;
        if (!data) setError(true);
        else
          setText(data.tooLarge ? '（文面が大きすぎるため保存していません）' : (data.text ?? ''));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [districtId, prompt.systemInstructionSnapshotId]);

  if (error) return <p className="text-xs text-danger-700">文面を読み込めませんでした。</p>;
  if (text === null) return <p className="text-xs text-slate-500">読込中…</p>;
  return (
    <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap text-[11px] text-slate-700">
      {text}
      {'\n\n【画像の後ろに付けた指示】\n'}
      {prompt.userText}
    </pre>
  );
}

function RunPrompts({
  districtId,
  prompts,
}: {
  districtId: string;
  prompts: AnalysisRunPromptRef[];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  if (prompts.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        この結果は解析時の記録の仕組みより前のため、AI に送った内容は残っていません。
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-1">
      {prompts.map((p, i) => (
        <details
          key={`${p.role}-${i}`}
          className="rounded border border-slate-200 bg-slate-50 px-2 py-1"
          onToggle={(e) => {
            if ((e.currentTarget as HTMLDetailsElement).open) setOpenIndex(i);
          }}
        >
          <summary className="cursor-pointer text-xs text-slate-700">
            AI に送った内容: {p.label}
            {p.responseSchemaSnapshotId ? '（出力の型を指定）' : ''}
          </summary>
          {openIndex === i && <PromptText districtId={districtId} prompt={p} />}
        </details>
      ))}
    </div>
  );
}

export function StreetSurveyAnalysisHistoryCard({
  districtId,
  postId,
  currentRunId,
  surveyKey,
  additionalSurveyKeys = [],
  evaluation = null,
  valueField = null,
  valueLabel = '調査項目',
  labelOf,
}: StreetSurveyAnalysisHistoryCardProps) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  // 比べる 2 つの記録 (選んだ順)。
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const toggleCompare = (id: string) =>
    setCompareIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id].slice(-2),
    );

  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, 'districts', districtId, 'posts', postId, ANALYSIS_RUNS_COLLECTION))
      .then((snap) => {
        if (cancelled) return;
        const rows = snap.docs
          .map((d) => ({ id: d.id, run: d.data() as AnalysisRunDoc }))
          .sort((a, b) => (b.run.analyzedAt ?? '').localeCompare(a.run.analyzedAt ?? ''));
        setState({ status: 'ready', rows });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [districtId, postId]);

  return (
    <Card padding="md" className="mb-3">
      <h2 className="text-sm font-semibold text-slate-800 mb-1">解析の履歴</h2>
      {state.status === 'loading' && <p className="text-xs text-slate-500">読込中…</p>}
      {state.status === 'error' && (
        <p className="text-xs text-danger-700">解析の履歴を読み込めませんでした。</p>
      )}
      {state.status === 'ready' && state.rows.length === 0 && (
        <p className="text-xs text-slate-600">
          まだ記録がありません。次に解析（再解析）すると、ここに解析ごとの記録が残ります（再解析前の結果も残ります）。
        </p>
      )}
      {state.status === 'ready' &&
        compareIds.length === 2 &&
        (() => {
          const picked = compareIds
            .map((id) => state.rows.find((r) => r.id === id))
            .filter((r): r is RunRow => !!r)
            .sort((x, y) => (x.run.analyzedAt ?? '').localeCompare(y.run.analyzedAt ?? ''));
          if (picked.length !== 2) return null;
          const [older, newer] = picked as [RunRow, RunRow];
          const c = compareAnalysisRuns(older.run, newer.run, { evaluation, valueField, labelOf });
          const labels = evaluation?.polarityLabels ?? POLARITY_LABELS;
          return (
            <div className="mb-3 rounded border border-primary-200 bg-primary-50 px-2 py-2 text-xs text-slate-700">
              <p className="font-semibold text-slate-800">
                記録の比較: {formatDateTime(older.run.analyzedAt)} →{' '}
                {formatDateTime(newer.run.analyzedAt)}
              </p>
              <div className="mt-1 overflow-x-auto">
                <table className="text-xs">
                  <tbody>
                    <tr>
                      <th className="pr-3 text-left font-normal text-slate-500">件数</th>
                      <td className="pr-3">{c.counts[0]}</td>
                      <td className="pr-3">→ {c.counts[1]}</td>
                    </tr>
                    {evaluation && (
                      <tr>
                        <th className="pr-3 text-left font-normal text-slate-500">
                          {evaluation.label}
                        </th>
                        <td className="pr-3">{polarityText(c.polarity[0], labels)}</td>
                        <td className="pr-3">→ {polarityText(c.polarity[1], labels)}</td>
                      </tr>
                    )}
                    {c.values.map((v) => (
                      <tr key={v.value}>
                        <th className="pr-3 text-left font-normal text-slate-500">
                          {valueLabel}: {v.label}
                        </th>
                        <td className="pr-3">{v.a}</td>
                        <td className={`pr-3 ${v.a !== v.b ? 'font-semibold' : ''}`}>→ {v.b}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-1">
                <span className="text-slate-500">解析時の設定の違い: </span>
                {c.differences.length > 0 ? c.differences.join('、') : 'なし'}
              </p>
            </div>
          );
        })()}
      {state.status === 'ready' && state.rows.length > 0 && (
        <p className="mb-1 text-[11px] text-slate-500">
          記録を 2 つ選ぶと、件数・{evaluation ? '評価の内訳・' : ''}
          調査項目ごとの件数と、解析時の設定の違いを比べられます。
        </p>
      )}
      {state.status === 'ready' && state.rows.length > 0 && (
        <ol className="grid grid-cols-1 gap-2">
          {state.rows
            .filter(({ run }) =>
              !surveyKey
                ? true
                : surveyKey === 'default'
                  ? !additionalSurveyKeys.includes(run.surveyKey ?? '')
                  : run.surveyKey === surveyKey,
            )
            .map(({ id, run }) => {
              const count = itemCount(run);
              const isCurrent = id === currentRunId;
              return (
                <li
                  key={id}
                  className={`min-w-0 rounded border px-2 py-2 text-xs text-slate-700 ${
                    isCurrent ? 'border-primary-300 bg-primary-50' : 'border-slate-200'
                  }`}
                >
                  <p className="font-semibold text-slate-800">
                    <label className="mr-2 inline-flex items-center gap-1 font-normal text-slate-600">
                      <input
                        type="checkbox"
                        checked={compareIds.includes(id)}
                        onChange={() => toggleCompare(id)}
                      />
                      比べる
                    </label>
                    {formatDateTime(run.analyzedAt)}
                    <span className="ml-2 font-normal text-slate-600">
                      {ANALYSIS_RUN_ORIGIN_LABELS[run.origin] ?? ''}・{pipelineLabel(run.pipeline)}
                      {count !== null ? `・${count} 件` : ''}
                      {isCurrent ? '・表示中の結果' : ''}
                    </span>
                  </p>
                  <dl className="mt-1 grid grid-cols-1 gap-y-0.5">
                    {run.config?.outputFormat && (
                      <div>
                        <dt className="inline text-slate-500">出力フォーマット: </dt>
                        <dd className="inline">{formatSource(run.config.outputFormat)}</dd>
                      </div>
                    )}
                    {run.config && run.config.masters.length > 0 && (
                      <div>
                        <dt className="inline text-slate-500">カテゴリマスタ: </dt>
                        <dd className="inline">
                          {run.config.masters.filter((m) => m.inPrompt).length === 0
                            ? 'なし'
                            : run.config.masters
                                .filter((m) => m.inPrompt)
                                .map((m) => (
                                  <details key={m.id} className="inline-block align-top mr-2">
                                    <summary className="cursor-pointer">{formatSource(m)}</summary>
                                    <MasterSnapshotView districtId={districtId} master={m} />
                                  </details>
                                ))}
                        </dd>
                      </div>
                    )}
                    {run.config?.surveyTemplate && (
                      <div>
                        <dt className="inline text-slate-500">机上調査テンプレート: </dt>
                        <dd className="inline">{formatSource(run.config.surveyTemplate)}</dd>
                      </div>
                    )}
                    {run.config?.settings && (
                      <div>
                        <dt className="inline text-slate-500">解析設定の版: </dt>
                        <dd className="inline">
                          {run.config.settings.version !== null
                            ? `第 ${run.config.settings.version} 版`
                            : '版不明'}
                        </dd>
                      </div>
                    )}
                    {run.config?.model && (
                      <div>
                        <dt className="inline text-slate-500">AI モデル: </dt>
                        <dd className="inline">{run.config.model}</dd>
                      </div>
                    )}
                  </dl>
                  <div className="mt-1">
                    <RunPrompts districtId={districtId} prompts={run.prompts ?? []} />
                  </div>
                </li>
              );
            })}
        </ol>
      )}
    </Card>
  );
}
